import React from "react";
import {
  getCompatibleVariants,
  isOptionValueAvailable,
  normalizeOptionValue,
} from "@/lib/variantSelection";

/**
 * VariantSelector — dynamic option group pill picker.
 * Read-only presentation for Sprint 3. Does NOT decide cart wiring.
 *
 * Props:
 *   optionGroups   : product.option_groups (array of {name, values})
 *   variants       : product.variants (public-sanitized)
 *   selection      : { [groupName]: value }
 *   onSelect       : (groupName, value) => void
 */
export default function VariantSelector({ optionGroups, variants, selection, onSelect }) {
  if (!Array.isArray(optionGroups) || !optionGroups.length) return null;

  return (
    <div className="mt-5 space-y-4" data-testid="variant-selector">
      {optionGroups.map((group) => (
        <div key={group.name} data-testid={`variant-group-${group.name}`}>
          <div className="text-[11px] uppercase tracking-widest brand-text-primary font-bold mb-2">
            {group.name}
            {selection?.[group.name] && (
              <span className="ml-2 text-[#5C5C5C] normal-case tracking-normal font-semibold">
                : {selection[group.name]}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {(group.values || []).map((value) => {
              const isSelected = normalizeOptionValue(selection?.[group.name]) === normalizeOptionValue(value);
              const hasCompatible = isOptionValueAvailable(variants, selection || {}, group.name, value);
              // Determine "sold out" for this candidate when selection allows unique compat check.
              const compatIfChosen = getCompatibleVariants(variants, { ...(selection || {}), [group.name]: value });
              const anyStock = compatIfChosen.some((v) => (Number(v.stock) || 0) > 0);
              const soldOut = hasCompatible && !anyStock;
              const disabled = !hasCompatible; // truly impossible combination

              return (
                <button
                  key={`${group.name}-${value}`}
                  type="button"
                  onClick={() => !disabled && onSelect(group.name, value)}
                  disabled={disabled}
                  aria-pressed={isSelected}
                  aria-label={`${group.name} ${value}${soldOut ? " (stok habis)" : ""}${disabled ? " (tidak tersedia)" : ""}`}
                  data-testid={`variant-option-${group.name}-${value}`}
                  data-selected={isSelected ? "true" : "false"}
                  data-soldout={soldOut ? "true" : "false"}
                  className={[
                    "relative min-w-[56px] px-4 py-2 rounded-xl text-sm font-semibold transition-all",
                    "border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-primary)]",
                    isSelected
                      ? "brand-bg-primary text-white brand-border-primary shadow-sm"
                      : "bg-white border-[#E5E5E5] text-[#1A1A1A] hover:brand-border-primary",
                    disabled ? "opacity-40 cursor-not-allowed line-through" : "",
                    soldOut && !isSelected ? "text-[#5C5C5C]" : "",
                  ].filter(Boolean).join(" ")}
                >
                  {value}
                  {soldOut && (
                    <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-[#DC2F02]">
                      Habis
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
